'use client'

import { type CSSProperties, useEffect, useRef } from 'react'
import { IconX } from '@tabler/icons-react'
import clsx from 'clsx'

import LinkButton, { type LinkButtonProps } from 'shared/ui/Button/LinkButton'
import { addTestId } from 'shared/utils/tests'

import useCTAParams from '../hooks/useCTAParams'
import { useRibbon } from '../providers/useRibbon'

import styles from './Ribbon.module.scss'

const StyledLinkButton = ({
  $color,
  className,
  style,
  ...props
}: LinkButtonProps & { $color?: string }) => (
  <LinkButton
    className={clsx(styles.ctaButton, className)}
    style={
      { '--ribbon-cta-color': $color || undefined, ...style } as CSSProperties
    }
    {...props}
  />
)

const Ribbon = () => {
  const { ribbon, hideRibbon } = useRibbon()
  const { href: ctaHref, text } = useCTAParams(ribbon)

  const lastBgColorRef = useRef<undefined | string>(
    ribbon?.backgroundColor?.color
  )

  useEffect(() => {
    if (ribbon) {
      lastBgColorRef.current = ribbon.backgroundColor?.color
    }
  }, [ribbon])

  //during close ribbon no changes of bg color, so we use lastBgColorRef.current
  const bgColor = ribbon
    ? ribbon.backgroundColor?.color
    : lastBgColorRef.current

  return (
    <div
      className={styles.container}
      style={{ '--ribbon-bg': bgColor || undefined } as CSSProperties}
      {...addTestId('ribbon')}
    >
      <div
        className={styles.content}
        style={
          {
            '--ribbon-color': ribbon?.textColor?.color || undefined
          } as CSSProperties
        }
      >
        <div className={styles.textContent}>
          <div className={styles.desktopContent}>
            <p>{ribbon?.desktopContent || ''}</p>
          </div>
          <div className={styles.onlyTabletContent}>
            <p>{ribbon?.tabletContent || ''}</p>
          </div>
          <div className={styles.mobileContent}>
            <p>{ribbon?.mobileContent || ''}</p>
          </div>
        </div>
        {ctaHref && (
          <StyledLinkButton
            href={ctaHref}
            variant='textInherit'
            size='small'
            fullWidth
            $color={ribbon?.cta?.textColor?.color}
          >
            {text}
          </StyledLinkButton>
        )}
      </div>
      <IconX
        className={styles.closeButton}
        style={
          {
            '--ribbon-close-color': ribbon?.closeButtonColor?.color || undefined
          } as CSSProperties
        }
        onClick={hideRibbon}
        aria-roledescription='close'
        aria-label='close'
        role='button'
      />
    </div>
  )
}

export default Ribbon
